// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * API endpoint for uploading asset metadata from CSV files.
 * Provides functionality to update metadata fields for assets based on
 * their serial numbers and product type.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { sparseAssetSchema } from '@/asset/api/types';
import { z } from 'zod';

const PATH = '/v1/import/asset-metadata-upload';

/**
 * Schema for a failed row in the upload process
 */
const rowValidationFailure = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

const assetMetadataResponseItem = z.object({
  serial: z.string(),
  index: z.number(),
  validatedAsset: sparseAssetSchema.nullable(),
  fieldsUpdated: z.record(z.string(), z.boolean()).nullable(),
  errors: z.array(rowValidationFailure),
});

/**
 * Schema for the asset metadata upload result
 */
const assetMetadataUploadResultSchema = z.object({
  success: z.boolean(),
  items: z.array(assetMetadataResponseItem),
  structureErrors: z.array(rowValidationFailure),
});

export type AssetMetadataUploadResult = z.infer<
  typeof assetMetadataUploadResultSchema
>;

export const uploadAssetMetadataResponseSchema = z.object({
  data: assetMetadataUploadResultSchema,
  metadata: responseMetadataSchema,
});

/**
 * Uploads asset metadata from a CSV file.
 * This function handles the API call to process the CSV file and update
 * asset metadata based on the provided product type and serial numbers.
 *
 * @param formData - FormData containing the CSV file and productId
 * @returns Promise resolving to upload result with success status and affected assets
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function uploadAssetMetadata(
  formData: FormData
): Promise<AssetMetadataUploadResult> {
  const response = await api.post(PATH, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const data = await parseResponse(
    uploadAssetMetadataResponseSchema,
    response.data,
    {
      schemaName: 'UploadAssetMetadata',
      endpoint: PATH,
    }
  );
  return data.data;
}
